'use client';

/**
 * VersionCombobox - Unified searchable version picker
 *
 * A combobox component for selecting versions with built-in search functionality.
 * Supports filtering by file name, system tags, and user tags using '+' for AND logic.
 *
 * Features:
 * - Search by file name, system tags, or user tag names
 * - AND logic with '+' separator (e.g., "YR25+wk40")
 * - Grouped display: System tags (current/previous) and Versions
 * - Colored tag badges for user-defined tags
 * - Keyboard navigation support via cmdk
 *
 * @example
 * <VersionCombobox
 *   versions={versions}
 *   value={selectedVersion}
 *   onChange={setSelectedVersion}
 * />
 */

import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from '../button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../command';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { LabelBadge } from '../label-badge';
import { TagBadge } from '../tag-badge';
import type { VersionComboboxProps, VersionOption } from './types';
import {
  filterVersions,
  findCurrentVersion,
  findPreviousVersion,
  formatVersionDate,
  formatVersionName,
  getSearchableText,
  getShortSha,
  parseSearchQuery,
  resolveSelectedVersion,
  sortVersionsByDate,
  SYSTEM_TAG_CURRENT,
  SYSTEM_TAG_PREVIOUS,
  VERSION_SEARCH_PLACEHOLDER,
} from './utils';

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Renders the selected value in the trigger button.
 *
 * When a system tag (current/previous) is selected but no version has that tag,
 * falls back to displaying the latest version with a "(latest)" indicator.
 */
function SelectedValueDisplay({
  value,
  versions,
}: {
  value: string;
  versions: VersionOption[];
}) {
  const { version, isFallback } = resolveSelectedVersion(value, versions);

  // No version found at all
  if (!version) {
    // If it's a system tag with no versions, show placeholder
    if (value === SYSTEM_TAG_CURRENT || value === SYSTEM_TAG_PREVIOUS) {
      return <span className="text-muted-foreground">No versions available</span>;
    }
    // Otherwise show truncated ID
    return <span className="truncate font-mono text-xs">{getShortSha(value)}</span>;
  }

  // Show system tag badge when it's a direct system tag selection (not fallback)
  if (!isFallback && (value === SYSTEM_TAG_CURRENT || value === SYSTEM_TAG_PREVIOUS)) {
    return <LabelBadge label={value as 'current' | 'previous'} />;
  }

  // Show version info (either direct selection or fallback)
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="truncate">{formatVersionName(version)}</span>
      {version.system_tag && <LabelBadge label={version.system_tag} />}
      {isFallback && (
        <span className="text-muted-foreground text-xs">(latest)</span>
      )}
    </div>
  );
}

/**
 * Renders the tags row for a version item.
 * Shows up to 3 tags with overflow indicator.
 */
function VersionTagsRow({ tags }: { tags: VersionOption['tags'] }) {
  if (!tags || tags.length === 0) return null;

  const visibleTags = tags.slice(0, 3);
  const overflowCount = tags.length - 3;

  return (
    <div className="flex gap-1 flex-wrap">
      {visibleTags.map((tag) => (
        <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
      ))}
      {overflowCount > 0 && (
        <span className="text-xs text-muted-foreground">+{overflowCount}</span>
      )}
    </div>
  );
}

/**
 * Renders a single version item in the dropdown list.
 */
function VersionItem({
  version,
  isSelected,
  showDate,
  showTags,
}: {
  version: VersionOption;
  isSelected: boolean;
  showDate: boolean;
  showTags: boolean;
}) {
  const dateLabel = version.created_at
    ? formatVersionDate(version.created_at)
    : null;

  return (
    <div className="flex w-full items-center gap-2">
      {/* Selection indicator */}
      <Check
        className={cn(
          'h-4 w-4 shrink-0',
          isSelected ? 'opacity-100' : 'opacity-0'
        )}
      />

      {/* Version info */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Row 1: Name + System Tag */}
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{formatVersionName(version)}</span>
          {version.system_tag && <LabelBadge label={version.system_tag} />}
        </div>

        {/* Row 2: User Tags */}
        {showTags && <VersionTagsRow tags={version.tags} />}

        {/* Row 3: Date */}
        {showDate && dateLabel && (
          <span className="text-xs text-muted-foreground">{dateLabel}</span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Sentinel value for "none" selection in the combobox.
 * cmdk requires non-empty strings for CommandItem values, so we use this
 * internal marker and convert it to empty string ('') in the onChange callback.
 */
const NONE_VALUE = '__none__';

function VersionCombobox({
  versions,
  value,
  onChange,
  showSystemTagShortcuts = true,
  showTags = true,
  showDate = true,
  allowNone = false,
  noneLabel = 'None',
  searchPlaceholder = VERSION_SEARCH_PLACEHOLDER,
  placeholder = 'Select version...',
  disabled,
  className,
  align = 'start',
  matchTriggerWidth = true,
}: VersionComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Sort versions by creation date (newest first)
  const sortedVersions = useMemo(
    () => sortVersionsByDate(versions),
    [versions]
  );

  // Filter versions based on search query
  const { filtered: filteredVersions, isFiltered } = useMemo(
    () => filterVersions(sortedVersions, search),
    [sortedVersions, search]
  );

  // Find system tag versions
  const currentVersion = useMemo(
    () => findCurrentVersion(versions),
    [versions]
  );
  const previousVersion = useMemo(
    () => findPreviousVersion(versions),
    [versions]
  );

  const hasCurrent = !!currentVersion;
  const hasPrevious = !!previousVersion;

  // Determine which system tags match the search
  const systemTagsMatchSearch = useMemo(() => {
    if (!isFiltered) {
      return { current: true, previous: true };
    }

    const { terms } = parseSearchQuery(search);

    const versionMatchesAllTerms = (
      version: VersionOption | undefined,
      systemLabel: string
    ) => {
      return terms.every((term) => {
        // Match the system label
        if (systemLabel.includes(term)) return true;
        // Match the version's data
        if (version) {
          const searchableText = getSearchableText(version);
          if (searchableText.includes(term)) return true;
        }
        return false;
      });
    };

    return {
      current: versionMatchesAllTerms(currentVersion, 'current'),
      previous: versionMatchesAllTerms(previousVersion, 'previous'),
    };
  }, [search, isFiltered, currentVersion, previousVersion]);

  // Should we show the system tags group?
  const showSystemGroup =
    showSystemTagShortcuts &&
    (hasCurrent || hasPrevious) &&
    (!isFiltered ||
      systemTagsMatchSearch.current ||
      systemTagsMatchSearch.previous);

  // Handle selection
  const handleSelect = (selectedValue: string) => {
    // Convert internal "none" value to empty string for external API
    onChange(selectedValue === NONE_VALUE ? '' : selectedValue);
    setOpen(false);
    setSearch('');
  };

  // Check if anything matches
  const hasResults =
    filteredVersions.length > 0 ||
    (showSystemGroup &&
      ((hasCurrent && systemTagsMatchSearch.current) ||
        (hasPrevious && systemTagsMatchSearch.previous)));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          {value ? (
            <SelectedValueDisplay value={value} versions={versions} />
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn('p-0', matchTriggerWidth && 'w-(--radix-popover-trigger-width)')}
        align={align}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />

          <CommandList>
            {/* Empty state */}
            {!hasResults && !allowNone && <CommandEmpty>No versions found.</CommandEmpty>}

            {/* None option - allows clearing selection */}
            {allowNone && (
              <CommandGroup>
                <CommandItem
                  value={NONE_VALUE}
                  onSelect={handleSelect}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !value || value === ''
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                  <span className="text-muted-foreground">{noneLabel}</span>
                </CommandItem>
              </CommandGroup>
            )}

            {/* System Tags Group */}
            {showSystemGroup && (
              <CommandGroup heading="System">
                {hasCurrent && systemTagsMatchSearch.current && (
                  <CommandItem
                    value={SYSTEM_TAG_CURRENT}
                    onSelect={handleSelect}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === SYSTEM_TAG_CURRENT
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <LabelBadge label="current" />
                  </CommandItem>
                )}
                {hasPrevious && systemTagsMatchSearch.previous && (
                  <CommandItem
                    value={SYSTEM_TAG_PREVIOUS}
                    onSelect={handleSelect}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === SYSTEM_TAG_PREVIOUS
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <LabelBadge label="previous" />
                  </CommandItem>
                )}
              </CommandGroup>
            )}

            {/* Versions Group */}
            {filteredVersions.length > 0 && (
              <CommandGroup heading="Versions">
                {filteredVersions.map((version) => (
                  <CommandItem
                    key={version.id}
                    value={version.id}
                    onSelect={handleSelect}
                  >
                    <VersionItem
                      version={version}
                      isSelected={value === version.id}
                      showDate={showDate}
                      showTags={showTags}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { VersionCombobox };
